import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import * as i18n from "../common/i18n";
import MaterialTable from 'material-table';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import { reactLocalStorage } from 'reactjs-localstorage';
import ReloadIcon from '@material-ui/icons/Replay';
import Chip from '@material-ui/core/Chip';
import * as validator from "validator";
import { CsvBuilder } from 'filefy';
import gql from 'graphql-tag';
import Message from "./Message";
import * as graphql from '../common/graphql';
import * as helper from "../common/helper";
import { withIAM, isInRoles, ROLE_ADMIN, ROLE_READER, ROLE_OWNER, ROLE_ASSISTANT, ROLE_EDITOR } from "../common/iam";

const styles = theme => ({
});

const defaults = {
    id: true,
    address: false,
    zipCode: false,
    place: false,
    trustee: false,
    tenant: false,
    pageSize: 20,
};

const defaultsPropertyName = 'p71.buildings.defaults';

class Buildings extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
            exporting: false,
            progressValue: 0,
            userSettings: reactLocalStorage.getObject(defaultsPropertyName, defaults),
        };

        this.tableRef = React.createRef();
    }

    default(id) {
        return this.state.userSettings[id] !== undefined ? this.state.userSettings[id] : defaults[id];
    }

    setDefault(id, value) {

        const newUserSettings = { ...this.state.userSettings };
        newUserSettings[id] = value;

        this.setState({ userSettings: newUserSettings }, () => {
            reactLocalStorage.setObject(defaultsPropertyName, newUserSettings);
        });
    }

    getInput = (data) => {

        try {

            if (
                validator.isEmpty(`${data.street}`) ||
                validator.isEmpty(`${data.zipCode}`) ||
                validator.isEmpty(`${data.place}`) ||
                validator.isEmpty(`${data.tenant}`)
            ) {
                throw new Error(i18n.get("buildings.error.validation"));
            }

            const input = {}

            helper.set(input, data, "street");
            helper.set(input, data, "zipCode");
            helper.set(input, data, "place");
            helper.set(input, data, "trustee", (t => Array.isArray(t) && t.length > 0 ? t[0]["value"] : t["id"]));
            helper.set(input, data, "tenant", (t => Array.isArray(t) ? t[0]["value"] : t["id"]));
            if (!input["trustee"]) input["trustee"] = null

            return input;

        } catch (error) {
            throw new Error(i18n.get("buildings.error.validation"))
        }
    }

    mapField(column) {
        return column;
    }
    
    getTenantSuggestions = async (value) => {
        try {
            const where = `[{column: "company", comparator: LIKE, string: "${value}"}]`;

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                query {
                    tenants(first: 5, offset: 0, where:${where} orderBy: [{column: "id", direction: ASC}]) {
                        totalCount
                        elements {
                            ... on Tenant {
                                id
                                company

                           }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            });

            return result.data.tenants.elements.map((element) => {
                return {
                    name: helper.tenantChipOf(element.company, element.id),
                    value: element.id,
                };
            });
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getDefaultSuggestions = () => {
        return [];
    }

    hasChildren = async (id) => {
        try {

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                variables: {
                    "id": id,
                },
                query: gql`
                query building($id: ID!) { 
                    building (id: $id) {
                        parcels {
                            id
                        }
                }
            }
            `,
                fetchPolicy: "no-cache"
            });

            return result.data.building.parcels.length > 0

        } catch (error) {
            console.log(error.message);
            return false
        }
    }

    exportCsv = async (columns, data) => {

        try {

            this.setState({
                exporting: true,
                progressValue: 0
            });

            const allBuildings = [];
            let totalCount = 0;
            const first = 100;
            let offset = 0

            this.showMessage("Export wird angestoßen. Bitte verlassen Sie die Seite nicht, solange der Exportvorgang läuft.", "info");

            do {
                const filters = []
                for (const column of columns) {
                    if (column.tableData.filterValue !== undefined && column.tableData.filterValue !== null) {
                        filters.push({
                            column: column,
                            field: column.field,
                            type: column.type,
                            comparator: column.comparator,
                            value: column.tableData.filterValue
                        })
                    }
                }

                const find = helper.whereString(filters, this.mapField, ["id"]);

                const result = await graphql.clientWithToken(this.props.accessToken).query({
                    query: gql`
                    query {
                        buildings(first: 100, offset: ${offset}, orderBy: [{column: "id" direction: ASC}], where: ${find}) {
                            totalCount
                            elements {
                                ... on Building {
                                    id
                                    street
                                    zipCode
                                    place
                                    trustee {
                                        id
                                        company
                                    }
                                    tenant {
                                        id
                                        company
                                    }
                                }
                            }
                        }
                    }
                `,
                    fetchPolicy: "no-cache"
                });

                totalCount = result.data.buildings.totalCount;
                offset = offset + first;
                for (const building of result.data.buildings.elements) {
                    allBuildings.push(
                        {
                            id: building.id,
                            street: building.street,
                            zipCode: building.zipCode,
                            place: building.place,
                            trustee: building.trustee ? building.trustee.company : '-',
                            tenant: building.tenant ? building.tenant.company : '-',
                        }
                    )
                }

                if (totalCount !== 0) {
                    this.setState({
                        exporting: true,
                        progressValue: (allBuildings.length / totalCount) * 100
                    });
                }
            } while (allBuildings.length < totalCount);

            const builder = new CsvBuilder('Gebäude.csv')
                .setDelimeter(";")
                .setColumns(columns.map(columnDef => columnDef.title))
                .addRows(allBuildings.map(building => { return [building.street, building.zipCode, building.place, building.trustee, building.tenant, building.id ]})) 
                .exportFile();

            this.setState({
                exporting: false,
                progressValue: 0
            });
        } catch (error) {
            this.setState({
                exporting: false,
                progressValue: 0
            });
            this.showMessage(error.message, "error");
        }

    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    render() {

        return (
            <div style={{ maxWidth: '100%' }}>

                {this.state.exporting && <div style={{ width: "100%" }}><LinearProgress value={this.state.progressValue} variant={"determinate"} /></div>}

                <MaterialTable
                    components={{
                        Cell: helper.defaultCell,
                        FilterRow: helper.defaultFilter
                    }}
                    icons={helper.tableIcons}
                    onChangeRowsPerPage={pageSize => { this.setDefault("pageSize", pageSize) }}
                    onChangeColumnHidden={data => { this.setDefault(data["field"], data["hidden"]) }}
                    tableRef={this.tableRef}
                    localization={i18n.getLocalization()}

                    columns={[
                        {
                            title: i18n.get("buildings.street"),
                            field: 'street',
                            hidden: this.default("street"),
                            cellStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            headerStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            sorting: true,
                            filtering: true
                        },
                        {
                            title: i18n.get("buildings.zipCode"),
                            field: 'zipCode',
                            hidden: this.default("zipCode"),
                            cellStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            headerStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            sorting: true,
                            filtering: true
                        },
                        {
                            title: i18n.get("buildings.place"),
                            field: 'place',
                            hidden: this.default("place"),
                            cellStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            headerStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            sorting: true,
                            filtering: true
                        },
                        {
                            title: i18n.get("buildings.trustee"),
                            hidden: this.default("trustee"),
                            field: "trustee",
                            cellStyle: {
                                minWidth: '600px',
                                maxWidth: '600px',
                            },
                            headerStyle: {
                                minWidth: '600px',
                                maxWidth: '600px',
                            },
                            sorting: true,
                            filtering: true,
                            comparator: 'IS',
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.company} (${v.id})`,
                                            value: v.id,
                                        };
                                    }),
                                this.getTenantSuggestions,
                                this.getDefaultSuggestions
                            ),
                            render: rowData => { return rowData.trustee && rowData.trustee.company ? <Chip label={`${rowData.trustee.company} (${rowData.trustee.id})`} value={rowData.trustee.id} /> : null },
                            editComponent: (props) => {

                                return helper.chips(
                                    props,
                                    props.value ? [{ name: `${props.value.company} (${props.value.id})`, value: props.value.id }] : [],
                                    i18n.get("buildings.trustee"),
                                    null,
                                    this.getTenantSuggestions,
                                    this.getDefaultSuggestions,
                                )
                            }
                        },
                        {
                            title: i18n.get("buildings.tenant"),
                            hidden: this.default("tenant"),
                            field: "tenant",
                            cellStyle: {
                                minWidth: '600px',
                                maxWidth: '600px',
                            },
                            headerStyle: {
                                minWidth: '600px',
                                maxWidth: '600px',
                            },
                            sorting: true,
                            filtering: true,
                            filterValue: 'company',
                            comparator: 'IS',
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.company} (${v.id})`,
                                            value: v.id,
                                        };
                                    }),
                                this.getTenantSuggestions,
                                this.getDefaultSuggestions
                            ),
                            render: rowData => { return rowData.tenant && rowData.tenant.company ? <Chip label={`${rowData.tenant.company} (${rowData.tenant.id})`} value={rowData.tenant.id} /> : null },
                            editComponent: (props) => {

                                return helper.chips(
                                    props,
                                    props.value ? [{ name: `${props.value.company} (${props.value.id})`, value: props.value.id }] : [],
                                    i18n.get("buildings.tenant"),
                                    null,
                                    this.getTenantSuggestions,
                                    this.getDefaultSuggestions,
                                )
                            }
                        },
                        {
                            title: i18n.get("buildings.id"),
                            field: 'id',
                            hidden: this.default("id"),
                            cellStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            headerStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            defaultFilter: (this.props.pageData && this.props.pageData.id) || null,
                            editable: 'never',
                            sorting: true,
                            filtering: true
                        },
                    ]}
                    data={query =>
                        new Promise(async (resolve, reject) => {

                            try {


                                const where = helper.whereString(query['filters'], this.mapField, ["id"]);
                                let orderBy = helper.orderByString(query, this.mapField, 'id', true);
                                if (where === "[]") {
                                    orderBy = helper.orderByString(query, this.mapField, "id")
                                }

                                const result = await graphql.clientWithToken(this.props.accessToken).query({
                                    query: gql`
                                    query {
                                        buildings(first: ${query.pageSize}, offset: ${query.pageSize * query.page}, orderBy:${orderBy}, where:${where}) {
                                            totalCount
                                            elements {
                                                ... on Building {
                                                    id,
                                                    street,
                                                    zipCode
                                                    place
                                                    trustee {
                                                        id
                                                        company
                                                    }
                                                    tenant {
                                                        id
                                                        company
                                                    }
                                                    parcels {
                                                        id
                                                        name
                                                    }
                                               }
                                            }
                                        }
                                    }
                                `,
                                    fetchPolicy: "no-cache"
                                });

                                const searchResult = {
                                    page: query.page,
                                    data: result.data.buildings.elements.map(
                                        (element) => {
                                            return {
                                                id: element.id,
                                                street: element.street,
                                                zipCode: element.zipCode,
                                                place: element.place,
                                                tenant: element.tenant,
                                                trustee: element.trustee,
                                                parcels: element.parcels
                                            };
                                        }),
                                    totalCount: result.data.buildings.totalCount
                                }
                                resolve(searchResult);
                            } catch (error) {
                                this.showMessage(error.message, "error");
                                reject(error);
                            }
                        })
                    }
                    detailPanel={
                        rowData => {
                            return (
                                <div>
                                    <Paper>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="center">{i18n.get("buildings.parcel")}</TableCell>
                                                    <TableCell align="center">{i18n.get("buildings.parcel.id")}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rowData.parcels.map(row => (
                                                    <TableRow key={row.id}>
                                                        <TableCell align="center">{row.name}</TableCell>
                                                        <TableCell align="center" component="th" scope="row"> {row.id} </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                </div>
                            );
                        }
                    }
                    actions={[{
                        isFreeAction: true,
                        icon: () => { return <ReloadIcon /> },
                        tooltip: i18n.get("general.reload"),
                        onClick: async (event, rowData) => {
                            this.tableRef.current.onQueryChange();
                        }
                    }]}
                    title={i18n.get("buildings.title")}
                    options={{
                        addRowPosition: "first",
                        exportButton: !this.state.exporting,
                        filtering: true,
                        showTitle: false,
                        debounceInterval: 500,
                        search: false,
                        columnsButton: true,
                        exportCsv: this.exportCsv,
                        pageSize: this.default("pageSize"),
                        pageSizeOptions: [5, 20, 50]
                    }}

                    editable={{
                        isEditable: rowData => isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]),
                        isDeletable: rowData => isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]),
                        onRowAdd: isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]) ? newData =>
                            new Promise(async (resolve, reject) => {
                                try {

                                    const input = this.getInput(newData)

                                    const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                                        variables: {
                                            "input": input,
                                        },
                                        mutation: gql`
                                        mutation addBuilding($input: BuildingInput!) {
                                            addBuilding(input: $input) {
                                                id
                                            }
                                        }
                                    `,
                                    });

                                    this.showMessage(i18n.get("buildings.message.onRowAdd.success"), "success")
                                    resolve();

                                } catch (error) {
                                    this.showMessage(i18n.get("buildings.message.onRowAdd.error"), "error", error.message);
                                    reject(error);
                                }
                            }) : null,

                        onRowUpdate: (newData, oldData) =>
                            new Promise(async (resolve, reject) => {

                                try {

                                    const input = this.getInput(newData);

                                    const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                                        variables: {
                                            "id": newData.id,
                                            "input": input,
                                        },
                                        mutation: gql`
                                        mutation updateBuilding($id: ID!, $input: BuildingInput!) {
                                            updateBuilding(id: $id, input: $input) {
                                                id
                                            }
                                        }
                                    `,
                                    });

                                    this.showMessage(i18n.get("buildings.message.onRowUpdate.success"), "success")

                                    resolve(result);
                                } catch (error) {

                                    this.showMessage(i18n.get("buildings.message.onRowUpdate.error"), "error", error.message)
                                    reject(error);
                                }
                            }),

                        onRowDelete: oldData =>
                            new Promise(async (resolve, reject) => {

                                try {

                                    const hasChildren = await this.hasChildren(oldData.id)

                                    if (!hasChildren) {
                                        const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                                            variables: {
                                                "id": oldData.id,
                                            },
                                            mutation: gql`
                                            mutation deleteBuilding($id: ID!) {
                                                deleteBuilding(id: $id)
                                            }
                                        `,
                                        });

                                        this.showMessage(i18n.get("buildings.message.onRowDelete.success"), "success")

                                        resolve(result);

                                    } else {
                                        this.showMessage(i18n.get("buildings.message.onRowDelete.notEmpty"), "error")
                                        reject();
                                    }

                                } catch (error) {
                                    this.showMessage(i18n.get("buildings.message.onRowDelete.error"), "error", error.message)
                                    reject(error);
                                }
                            }),
                    }}

                />

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />
            </div>
        )
    }
}

Buildings.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(Buildings))